import { cx } from '@bolteu/clay-react';
import React, { createContext, useContext, useEffect, useState } from 'react';

/**
 * The class the Clay design-system stylesheet scopes its tokens/base rules to
 * (see scripts/clay/scope-css.mjs). Any element inside a `.clay-scope` resolves
 * Clay's `--color-*`/theme tokens; everything outside keeps the app's legacy
 * Kalep `:root` tokens.
 */
export const CLAY_SCOPE_CLASS = 'clay-scope';

/**
 * clay-react overlays (Dialog/Menu/Select/Tooltip/…) render through @base-ui
 * portals to `document.body`, which is *outside* any `<ClayScope>` subtree — so
 * without help they'd fall back to Kalep tokens. clay-react has no global portal
 * container provider (each `*.Portal` takes its own `container` prop), so a
 * scope exposes a body-level, `.clay-scope`-classed host through this context;
 * overlays inside a scope pass `container={useClayPortalContainer()}` to their
 * `*.Portal` to render into it (and thus stay Clay-styled).
 */
const ClayPortalContainerContext = createContext<HTMLElement | null>(null);

export function useClayPortalContainer(): HTMLElement | null {
  return useContext(ClayPortalContainerContext);
}

export interface ClayScopeProps {
  children: React.ReactNode;
  /** Extra classes on the scope element (layout, sizing, etc.). */
  className?: string;
}

/**
 * Marks a subtree as Clay-styled: Clay design tokens win inside it while the
 * rest of the app keeps its Kalep styling untouched. Wrap a migrated surface's
 * content once — descendants inherit via the cascade, so individual Clay
 * components need no wrapper of their own.
 */
export function ClayScope({ children, className }: ClayScopeProps) {
  const [portalHost, setPortalHost] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    const host = document.createElement('div');
    host.className = CLAY_SCOPE_CLASS;
    host.setAttribute('data-clay-portal-host', '');
    // Overlays portal into this body-level host, outside the scope subtree, so it
    // carries the same hardcoded `data-mode` as the scope element itself.
    host.setAttribute('data-mode', 'light');
    document.body.appendChild(host);
    setPortalHost(host);

    return () => host.remove();
  }, []);

  return (
    <ClayPortalContainerContext.Provider value={portalHost}>
      <div className={cx(CLAY_SCOPE_CLASS, className)} data-mode="light">
        {children}
      </div>
    </ClayPortalContainerContext.Provider>
  );
}
